varying vec2 vUv;

uniform float uTime;

vec4 permute(vec4 x){ return mod(((x*34.0)+1.0)*x, 289.0); }
vec4 taylorInvSqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314 * r; }
vec3 fade(vec3 t) { return t*t*t*(t*(t*6.0-15.0)+10.0); }

float cnoise(vec3 P)
{
    vec3 Pi0 = floor(P);
    vec3 Pi1 = Pi0 + vec3(1.0);
    Pi0 = mod(Pi0, 289.0);
    Pi1 = mod(Pi1, 289.0);
    vec3 Pf0 = fract(P);
    vec3 Pf1 = Pf0 - vec3(1.0);
    vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
    vec4 iy = vec4(Pi0.yy, Pi1.yy);
    vec4 iz0 = Pi0.zzzz;
    vec4 iz1 = Pi1.zzzz;

    vec4 ixy = permute(permute(ix) + iy);
    vec4 ixy0 = permute(ixy + iz0);
    vec4 ixy1 = permute(ixy + iz1);

    vec4 gx0 = ixy0 / 7.0;
    vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
    gx0 = fract(gx0);
    vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
    vec4 sz0 = step(gz0, vec4(0.0));
    gx0 -= sz0 * (step(0.0, gx0) - 0.5);
    gy0 -= sz0 * (step(0.0, gy0) - 0.5);

    vec4 gx1 = ixy1 / 7.0;
    vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
    gx1 = fract(gx1);
    vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
    vec4 sz1 = step(gz1, vec4(0.0));
    gx1 -= sz1 * (step(0.0, gx1) - 0.5);
    gy1 -= sz1 * (step(0.0, gy1) - 0.5);

    vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
    vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
    vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
    vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
    vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
    vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
    vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
    vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

    vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
    g000 *= norm0.x;
    g010 *= norm0.y;
    g100 *= norm0.z;
    g110 *= norm0.w;
    vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
    g001 *= norm1.x;
    g011 *= norm1.y;
    g101 *= norm1.z;
    g111 *= norm1.w;

    float n000 = dot(g000, Pf0);
    float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
    float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
    float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
    float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
    float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
    float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
    float n111 = dot(g111, Pf1);

    vec3 fade_xyz = fade(Pf0);
    vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
    vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
    float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x); 
    
    return 2.2 * n_xyz;
}

// ========== FUNZIONI PER DISEGNARE SMILE ==========

float circle(vec2 uv, vec2 center, float radius) {
    float dist = length(uv - center);
    return smoothstep(radius, radius - 0.02, dist);
}

float drawSmile(vec2 uv, vec2 center, float size, float timeOffset) {
    // Animazione: movimento su/giù e rotazione
    float yOffset = sin(uTime * 2.0 + timeOffset) * 0.08;
    float rotation = sin(uTime * 1.5 + timeOffset) * 0.2;
    
    vec2 localUv = uv - (center + vec2(0.0, yOffset));
    
    // Applica rotazione
    float cosR = cos(rotation);
    float sinR = sin(rotation);
    localUv = vec2(
        localUv.x * cosR - localUv.y * sinR,
        localUv.x * sinR + localUv.y * cosR
    );
    
    localUv /= size;
    
    // Faccia (cerchio giallo)
    float face = circle(localUv, vec2(0.0), 1.0);
    
    // Occhi neri
    float eyeLeft = circle(localUv, vec2(-0.35, 0.35), 0.18);
    float eyeRight = circle(localUv, vec2(0.35, 0.35), 0.18);
    float eyes = max(eyeLeft, eyeRight);
    
    // Bocca sorridente - arco parabolico curvo verso l'ALTO per un vero sorriso felice!
    float mouth = 0.0;
    for(float i = -0.5; i <= 0.5; i += 0.04) {
        float y = -0.25 + (i * i) * 1.2;  // Parabola invertita - SORRISO FELICE!
        float dist = distance(localUv, vec2(i, y));
        mouth += smoothstep(0.12, 0.08, dist);
    }
    mouth = clamp(mouth, 0.0, 1.0);
    
    // Combina: faccia gialla con occhi neri sottratti e bocca scura visibile che fa sorridere
    float smileResult = face * (1.0 - eyes * 0.85);  // Occhi come buchi
    smileResult = mix(smileResult, smileResult * 0.3, mouth * 0.8);  // Bocca scura che fa sorridere
    
    return max(smileResult, 0.0);
}

// =================================================

void main() {
    // ===== ONDE BLU ORIGINALI =====
    vec2 displacedUv = vUv + cnoise(vec3(vUv * 5.0, uTime * 0.2));
    float strength = cnoise(vec3(vUv * 2.3, uTime * 0.2));
    strength += step(-0.2, strength) * 0.9;
    strength = clamp(strength, 0.0, 1.0);
    vec3 waveColor = mix(vec3(0.7, 0.7, 0.7), vec3(0.07, 0.35, 0.67), strength);
    
    // ===== AGGIUNGI SMILE ANIMATI =====
    float smilesLayer = 0.0;
    
    // 3 smile con offset temporali diversi per movimento asincrono
    smilesLayer += drawSmile(vUv, vec2(0.25, 0.5), 0.12, 0.0);
    smilesLayer += drawSmile(vUv, vec2(0.5, 0.5), 0.12, 2.0);
    smilesLayer += drawSmile(vUv, vec2(0.75, 0.5), 0.12, 4.0);
    
    smilesLayer = clamp(smilesLayer, 0.0, 1.0);
    
    // Colore smile: giallo brillante
    vec3 smileColor = vec3(1.0, 0.85, 0.0);
    
    // ===== COMBINA ONDE E SMILE =====
    vec3 finalColor = mix(waveColor, smileColor, smilesLayer);

    gl_FragColor = vec4(finalColor, 1.0);
}
